import {
  MediaData,
  MediaPickerComponent,
  MediaType,
  NewMessage,
  PickerMode,
  Severity,
} from '@gripp/shared-logic';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FlatList,
  Platform,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { IconButton } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { useRoleHex } from '../../themes';
import { AddButton } from '../crop-circle/AddButton';
import { colors as CropCircleColors } from '../crop-circle/tokens/colors';
import { RenderMediaItem } from '../media/renderMediaItem';
import { useIssueFooterDraft } from './useIssueFooterDraft';
import { MentionInput } from '../mentionInputField';
import { IssueFooterMobile } from './issueFooterMobile';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

export type IssueFooterProps = {
  active?: boolean;
  onNewMessage: (message: NewMessage) => Promise<void>;
  mediaPicker: MediaPickerComponent;
  acceptedMediaTypes?: MediaType[];
  workspaceId?: string;
  /**
   * The activity being composed in, if any. Forwarded to the mention field so
   * a guest who has a live grant on this activity is mentionable inside it;
   * omit for a not-yet-created activity (there is nothing to grant against).
   */
  issueId?: string;
  mode?: PickerMode;
  containerStyle?: StyleProp<ViewStyle>;
  maxImagePreviewWidth?: number;
  focusOnMessage?: boolean;
  /** Placeholder text for the compose input. Defaults to t('common.message'). */
  placeholder?: string;
  pickDocument?: (callbacks: {
    setUploading: (uploading: boolean) => void;
    onProgress: (mediaId: string, progress: number) => void;
  }) => Promise<MediaData | null>;
  /**
   * Mobile only. Summarising is contextual: the composer renders its AI icon
   * only where a host actually has something to summarise, so leave this unset
   * (as every caller does today, there being no summarise service yet) and the
   * icon stays hidden rather than presenting a dead control.
   */
  onSummarize?: () => void;
  /** Native asset Chat only, after flag/access admission. The host captures the
   * severity when onNewMessage starts and resets it only on successful posting,
   * provided that selection has not changed in the meantime. Threads omit this. */
  chatPosting?: {
    severity?: Severity;
    onSeverityChange: (severity?: Severity) => void;
  };
};

/**
 * Platform router: native gets the redesigned pill-to-card composer
 * (issueFooterMobile.tsx, Figma V6 "Text Navigation"); web keeps the legacy
 * add-button + pill layout below until it gets its own redesign pass.
 */
export const IssueFooter = (props: IssueFooterProps) =>
  Platform.OS === 'web' ? (
    <IssueFooterWeb
      key={JSON.stringify([props.workspaceId, props.issueId])}
      {...props}
    />
  ) : (
    <IssueFooterMobile {...props} />
  );

const IssueFooterWeb = (props: IssueFooterProps) => {
  const textOnInverseHex = useRoleHex('textOnInverse');
  const { t } = useTranslation();
  const {
    handleAdd,
    handleUploadProgress,
    messageMedia,
    handleDelete,
    reset: resetDraft,
    message,
    setMessage,
    canSend,
    setUploading,
    send: submit,
  } = useIssueFooterDraft(props.onNewMessage, props.active);

  const [showPreviewGallery, setShowThumbnailPreview] = useState(false);
  const [isMultiline, setIsMultiline] = useState<boolean>(false);
  const [inputHeight, setInputHeight] = useState<number>(
    minHeightInputContainerPhone
  );

  const attachmentOpenRef = useRef<(() => void) | null>(null);

  // Always keep inputHeight at or above the pill's minimum so the TextInput
  // (which receives this value as an explicit height) never shrinks the pill.
  // onContentSizeChange reports ~20 px for a single line which would otherwise
  // collapse the 56 px container on the first keystroke.
  const handleSetInputHeight = useCallback(
    (h: number) => setInputHeight(Math.max(h, minHeightInputContainerPhone)),
    []
  );

  const MediaPicker = props.mediaPicker;
  const pickerMode = props.mode || PickerMode.CAMERA;
  let pickerIcon;

  const messageInputRef = useRef(null);

  // Footer height grows with the input on both platforms.
  // paddingVertical: 16 (top + bottom = 32) accounts for the extra space.
  const footerHeight = inputHeight + 32;

  const hasVideoInPreview = messageMedia.some(
    (m) => m.type === MediaType.Video
  );
  const previewAreaHeight = hasVideoInPreview
    ? mediaContainerHeightWithVideo
    : mediaContainerHeight;

  switch (pickerMode) {
    case PickerMode.PICKER:
      pickerIcon = 'image-outline';
      break;
    case PickerMode.CAMERA:
      pickerIcon = 'camera-outline';
      break;
    case PickerMode.ATTACHMENT:
      pickerIcon = 'paperclip';
      break;
    default:
      pickerIcon = 'camera-outline';
      break;
  }

  // Crop circle redesign always uses plus icon
  pickerIcon = 'plus';

  useEffect(() => {
    setShowThumbnailPreview(messageMedia.length > 0);
  }, [messageMedia]);

  const send = async () => {
    if (await submit()) {
      dismissKeyboard();
      setIsMultiline(false);
      setInputHeight(minHeightInputContainerPhone);
    }
  };

  const dismissKeyboard = () => {
    messageInputRef.current?.blur();
  };

  const deleteMediaItem = (mediaId: string) => {
    handleDelete(mediaId);
  };

  return (
    <View
      style={[
        {
          minHeight: showPreviewGallery
            ? footerHeight + previewAreaHeight + 10
            : footerHeight,
          height: showPreviewGallery
            ? footerHeight + previewAreaHeight + 10
            : footerHeight,
          paddingTop: 0,
          paddingBottom: 8,
        },
        styles.footer,
        props.containerStyle,
      ]}
    >
      {showPreviewGallery && (
        <View
          style={{
            width: '100%',
            paddingLeft: 64,
            paddingRight: 16,
            marginTop: 10,
          }}
        >
          <View
            style={[
              styles.mediaContainer,
              {
                flexDirection: 'row',
                height: previewAreaHeight,
                minHeight: previewAreaHeight,
              },
            ]}
          >
            {messageMedia.length > 0 && (
              <FlatList
                horizontal={true}
                data={messageMedia}
                style={styles.mediaList}
                renderItem={({ item }) => (
                  <View style={styles.mediaItemContainer} key={item.id}>
                    <RenderMediaItem
                      mediaItem={item}
                      key={item.id}
                      deleteMediaItem={deleteMediaItem}
                      hasLightbox
                      canDelete
                      displayWidth={
                        item.type === MediaType.Video ? 200 : undefined
                      }
                    />
                  </View>
                )}
              ></FlatList>
            )}
          </View>
        </View>
      )}

      <View
        style={[
          styles.footerContainer,
          { alignItems: isMultiline ? 'flex-end' : 'center' },
        ]}
      >
        <View style={styles.imagePickerContainer}>
          <AddButton
            onPress={() => attachmentOpenRef.current?.()}
            accessibilityLabel="Attach file"
          />
          <View style={styles.headlessPicker} pointerEvents="none">
            <MediaPicker
              iconColor={Colors.role.textOnBrand}
              cardStyle={'contained'}
              mode={pickerMode}
              style={styles.imagePickerIcon}
              icon={pickerIcon}
              iconSize={30}
              setMedia={handleAdd}
              onUploadError={handleDelete}
              setProgress={handleUploadProgress}
              setUploading={(busy) => setUploading('attachment', busy)}
              workspaceId={props.workspaceId}
              dismissKeyboard={dismissKeyboard}
              acceptedMediaTypes={props.acceptedMediaTypes}
              onPickerReady={(opener) => {
                attachmentOpenRef.current = opener;
              }}
            />
          </View>
        </View>
        <View
          style={[
            styles.messageContainer,
            {
              borderRadius: isMultiline
                ? inputContainerBorderRadiusMultiline
                : inputContainerBorderRadiusPhone,
              height: inputHeight,
              // Prevent the pill from shrinking below the initial single-line
              // height when onContentSizeChange reports a small value (~39 px
              // for the first line before the formula adds its padding offset).
              minHeight: minHeightInputContainerPhone,
            },
          ]}
        >
          <View style={styles.inputContainer}>
            <MentionInput
              inputFieldText={message}
              setInputFieldText={setMessage}
              setIsMultiline={setIsMultiline}
              inputHeight={inputHeight}
              setInputHeight={handleSetInputHeight}
              send={send}
              placeholder={props.placeholder ?? t('common.message')}
              issueId={props.issueId}
            />
          </View>
          {canSend && (
            <View style={styles.sendMessage}>
              <IconButton
                icon="close"
                iconColor={Colors.white}
                style={{
                  margin: 0,
                  width: 32,
                  height: 32,
                  backgroundColor: Colors.role.borderStrong,
                  borderRadius: 99,
                }}
                onPress={() => {
                  resetDraft();
                  setIsMultiline(false);
                  setInputHeight(minHeightInputContainerPhone);
                }}
                size={16}
              />
              <IconButton
                icon="arrow-up"
                iconColor={textOnInverseHex}
                style={{
                  margin: 0,
                  width: 32,
                  height: 32,
                  backgroundColor: Colors.role.surfaceInverse,
                  borderRadius: 99,
                }}
                onPress={send}
                size={18}
              />
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

const inputContainerBorderRadiusPhone = 99;
const inputContainerBorderRadiusMultiline = 16;
const mediaContainerHeight = 120;
/** Fits 200px-wide video preview + controls (200*9/16 + 64 ≈ 176) with margin */
const mediaContainerHeightWithVideo = 200;
const minHeightInputContainerPhone = 56;

const styles = themedStyleSheet((role) => ({
  container: {
    ...StyleSheet.absoluteFillObject,
  },
  imagePickerContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    height: 56,
    width: 56,
  },
  imagePickerIcon: {
    height: 56,
    maxHeight: 56,
    width: 56,
    backgroundColor: CropCircleColors.brand.core,
    borderRadius: 99,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headlessPicker: {
    position: 'absolute',
    top: -9999,
    left: -9999,
    width: 0,
    height: 0,
    opacity: 0,
  },
  sendMessage: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'flex-end',
    paddingBottom: 12,
    gap: 8,
  },
  messageContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: role.surfaceHover,
    borderRadius: 99,
    paddingLeft: 24,
    paddingRight: 12,
  },
  inputContainer: {
    flex: 5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mediaContainer: {
    height: mediaContainerHeight,
    alignItems: 'center',
    justifyContent: 'flex-start',
    borderRadius: 12,
    paddingLeft: 16,
    paddingBottom: 16,
    backgroundColor: role.surfaceHover,
  },
  footer: {
    backgroundColor: role.canvas,
    ...Platform.select({
      default: {
        minHeight: 60,
      },
    }),
  },
  footerContainer: {
    flexDirection: 'row' as const,
    flex: 1,
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  mediaList: {
    width: '100%',
    height: '100%',
    flex: 1,
  },
  mediaItemContainer: {
    alignSelf: 'center',
    marginLeft: 8,
  },
}));
